import cntl from "cntl";
import PropTypes from "prop-types";
import { Badge } from "../../atoms";
import { BADGE_VARIANTS, COLOR } from "../../constants";

const propTypes = {
  icon: PropTypes.object,
  variant: PropTypes.oneOf([BADGE_VARIANTS.solid, BADGE_VARIANTS.surface, BADGE_VARIANTS.soft, BADGE_VARIANTS.outline]),
  color: PropTypes.oneOf([
    COLOR.gray,
    COLOR.info,
    COLOR.blue,
    COLOR.pink,
    COLOR.amber,
    COLOR.error,
    COLOR.primary,
    COLOR.success,
    COLOR.warning,
  ]),
};

function BadgeText({ icon, className, color = COLOR.gray, variant = BADGE_VARIANTS.soft, ...props }) {
  const badgeTextCn = () => cntl`
    ${style[variant]?.[color]?.label}
    ${style[variant]?.[color]?.border}
    ${style[variant]?.[color]?.background}
    ${className ? className : undefined}
  `;

  const style = {
    [BADGE_VARIANTS.soft]: {
      [COLOR.primary]: {
        background: "bg-primary-light-3 dark:bg-primary-dark-3",
        label: "text-primary-light-11 dark:text-primary-dark-11",
      },
      [COLOR.gray]: {
        background: "bg-gray-light-3 dark:bg-gray-dark-3",
        label: "text-gray-light-11 dark:text-gray-dark-11",
      },
      [COLOR.error]: {
        background: "bg-error-light-3 dark:bg-error-dark-3",
        label: "text-error-light-11 dark:text-error-dark-11",
      },
      [COLOR.success]: {
        background: "bg-success-light-3 dark:bg-success-dark-3",
        label: "text-success-light-11 dark:text-success-dark-11",
      },
      [COLOR.info]: {
        background: "bg-info-light-3 dark:bg-info-dark-3",
        label: "text-info-light-11 dark:text-info-dark-11",
      },
      [COLOR.warning]: {
        background: "bg-warning-light-3 dark:bg-warning-dark-3",
        label: "text-warning-light-11 dark:text-warning-dark-11",
      },
      [COLOR.blue]: {
        background: "bg-blue-light-3 dark:bg-blue-dark-3",
        label: "text-blue-light-11 dark:text-blue-dark-11",
      },
      [COLOR.amber]: {
        background: "bg-amber-light-3 dark:bg-amber-dark-3",
        label: "text-amber-light-11 dark:text-amber-dark-11",
      },
      [COLOR.pink]: {
        background: "bg-pink-light-3 dark:bg-pink-dark-3",
        label: "text-pink-light-11 dark:text-pink-dark-11",
      },
    },
    [BADGE_VARIANTS.solid]: {
      [COLOR.primary]: {
        label: "text-white",
        background: "bg-primary-light-9 dark:bg-primary-dark-9",
      },
      [COLOR.gray]: {
        label: "text-white",
        background: "bg-gray-light-9 dark:bg-gray-dark-9",
      },
      [COLOR.error]: {
        label: "text-white",
        background: "bg-error-light-9 dark:bg-error-dark-9",
      },
      [COLOR.success]: {
        label: "text-white",
        background: "bg-success-light-9 dark:bg-success-dark-9",
      },
      [COLOR.info]: {
        label: "text-white",
        background: "bg-info-light-9 dark:bg-info-dark-9",
      },
      [COLOR.warning]: {
        label: "text-white",
        background: "bg-warning-light-9 dark:bg-warning-dark-9",
      },
      [COLOR.blue]: {
        label: "text-white",
        background: "bg-blue-light-9 dark:bg-blue-dark-9",
      },
      [COLOR.amber]: {
        label: "text-gray-light-12 dark:text-gray-dark-12",
        background: "bg-amber-light-9 dark:bg-amber-dark-9",
      },
      [COLOR.pink]: {
        label: "text-white",
        background: "bg-pink-light-9 dark:bg-pink-dark-9",
      },
    },
    [BADGE_VARIANTS.surface]: {
      [COLOR.primary]: {
        background: "bg-primary-light-3 dark:bg-primary-dark-3",
        label: "text-primary-light-11 dark:text-primary-dark-11",
        border: "border border-primary-light-7 dark:border-primary-dark-7",
      },
      [COLOR.gray]: {
        background: "bg-gray-light-3 dark:bg-gray-dark-3",
        label: "text-gray-light-11 dark:text-gray-dark-11",
        border: "border border-gray-light-7 dark:border-gray-dark-7",
      },
      [COLOR.error]: {
        background: "bg-error-light-3 dark:bg-error-dark-3",
        label: "text-error-light-11 dark:text-error-dark-11",
        border: "border border-error-light-7 dark:border-error-dark-7",
      },
      [COLOR.success]: {
        background: "bg-success-light-3 dark:bg-success-dark-3",
        label: "text-success-light-11 dark:text-success-dark-11",
        border: "border border-success-light-7 dark:border-success-dark-7",
      },
      [COLOR.info]: {
        background: "bg-info-light-3 dark:bg-info-dark-3",
        label: "text-info-light-11 dark:text-info-dark-11",
        border: "border border-info-light-7 dark:border-info-dark-7",
      },
      [COLOR.warning]: {
        background: "bg-warning-light-3 dark:bg-warning-dark-3",
        label: "text-warning-light-11 dark:text-warning-dark-11",
        border: "border border-warning-light-7 dark:border-warning-dark-7",
      },
      [COLOR.blue]: {
        background: "bg-blue-light-3 dark:bg-blue-dark-3",
        label: "text-blue-light-11 dark:text-blue-dark-11",
        border: "border border-blue-light-7 dark:border-blue-dark-7",
      },
      [COLOR.amber]: {
        background: "bg-amber-light-3 dark:bg-amber-dark-3",
        label: "text-amber-light-11 dark:text-amber-dark-11",
        border: "border border-amber-light-7 dark:border-amber-dark-7",
      },
      [COLOR.pink]: {
        background: "bg-pink-light-3 dark:bg-pink-dark-3",
        label: "text-pink-light-11 dark:text-pink-dark-11",
        border: "border border-pink-light-7 dark:border-pink-dark-7",
      },
    },
    [BADGE_VARIANTS.outline]: {
      [COLOR.primary]: {
        background: "bg-transparent",
        label: "text-primary-light-11 dark:text-primary-dark-11",
        border: "border-1.5 border-primary-light-8 dark:border-primary-dark-8",
      },
      [COLOR.gray]: {
        background: "bg-transparent",
        label: "text-gray-light-11 dark:text-gray-dark-11",
        border: "border-1.5 border-gray-light-8 dark:border-gray-dark-8",
      },
      [COLOR.error]: {
        background: "bg-transparent",
        label: "text-error-light-11 dark:text-error-dark-11",
        border: "border-1.5 border-error-light-8 dark:border-error-dark-8",
      },
      [COLOR.success]: {
        background: "bg-transparent",
        label: "text-success-light-11 dark:text-success-dark-11",
        border: "border-1.5 border-success-light-8 dark:border-success-dark-8",
      },
      [COLOR.info]: {
        background: "bg-transparent",
        label: "text-info-light-11 dark:text-info-dark-11",
        border: "border-1.5 border-info-light-8 dark:border-info-dark-8",
      },
      [COLOR.warning]: {
        background: "bg-transparent",
        label: "text-warning-light-11 dark:text-warning-dark-11",
        border: "border-1.5 border-warning-light-8 dark:border-warning-dark-8",
      },
      [COLOR.blue]: {
        background: "bg-transparent",
        label: "text-blue-light-11 dark:text-blue-dark-11",
        border: "border-1.5 border-blue-light-8 dark:border-blue-dark-8",
      },
      [COLOR.amber]: {
        background: "bg-transparent",
        label: "text-amber-light-11 dark:text-amber-dark-11",
        border: "border-1.5 border-amber-light-8 dark:border-amber-dark-8",
      },
      [COLOR.pink]: {
        background: "bg-transparent",
        label: "text-pink-light-11 dark:text-pink-dark-11",
        border: "border-1.5 border-pink-light-8 dark:border-pink-dark-8",
      },
    },
  };

  return (
    <Badge className={badgeTextCn()} {...props}>
      {icon}
    </Badge>
  );
}

BadgeText.propTypes = propTypes;
export default BadgeText;
